import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './SiteCard.css';

const SiteCard = ({ site, onEdit, onDelete, onConnectAnalytics, onConnectAdSense, onConnectGSC }) => {
  const { user } = useAuth();
  const isDemoUser = user?.email === 'demo@example.com';
  const [showMenu, setShowMenu] = useState(false);

  const hasAnalytics = site.analyticsPropertyId;
  const hasAdSense = site.adsenseAccountId;
  const hasSearchConsole = site.searchConsoleSiteUrl || site.searchConsoleUrl;

  return (
    <div className="site-card">
      <div className="site-card-header">
        <div className="site-card-title">
          <h3>{site.name}</h3>
          {site.isPrimary && <span className="primary-badge">Primary</span>}
        </div>
        {!isDemoUser && (
          <div className="site-card-menu">
            <button 
              className="menu-button"
              onClick={() => setShowMenu(!showMenu)}
              aria-label="Site options"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <circle cx="10" cy="4" r="1.5" />
                <circle cx="10" cy="10" r="1.5" />
                <circle cx="10" cy="16" r="1.5" />
              </svg>
            </button>
            {showMenu && (
              <>
                <div className="menu-overlay" onClick={() => setShowMenu(false)} />
                <div className="menu-dropdown">
                  <button onClick={() => { onEdit(site); setShowMenu(false); }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708l-3-3zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207l6.5-6.5zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.499.499 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11l.178-.178z" />
                    </svg>
                    Edit Site
                  </button>
                  <button onClick={() => { onDelete(site); setShowMenu(false); }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" />
                    </svg>
                    Delete Site
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="site-card-url">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1.002 1.002 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4.018 4.018 0 0 1-.128-1.287z" />
          <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243L6.586 4.672z" />
        </svg>
        <span>{site.url}</span>
      </div>

      <div className="site-card-connections">
        <div className="connection-item">
          <div className="connection-status">
            <div className={`status-indicator ${hasAnalytics ? 'connected' : 'disconnected'}`} />
            <span>Google Analytics 4</span>
          </div>
          {hasAnalytics ? (
            <div className="connection-actions">
              <span className="connection-name">{site.analyticsPropertyName}</span>
              {!isDemoUser && (
                <button 
                  className="btn-reconnect"
                  onClick={() => onConnectAnalytics(site)}
                  title="Reconnect Analytics"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                    <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
                  </svg>
                </button>
              )}
            </div>
          ) : (
            <button 
              className="btn-connect"
              onClick={() => !isDemoUser && onConnectAnalytics(site)}
              disabled={isDemoUser}
            >
              Connect
            </button>
          )}
        </div>

        <div className="connection-item">
          <div className="connection-status">
            <div className={`status-indicator ${hasAdSense ? 'connected' : 'disconnected'}`} />
            <span>Google AdSense</span>
          </div>
          {hasAdSense ? (
            <div className="connection-actions">
              <span className="connection-name">{site.adsenseAccountName}</span>
              {!isDemoUser && (
                <button 
                  className="btn-reconnect"
                  onClick={() => onConnectAdSense(site)}
                  title="Reconnect AdSense"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                    <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
                  </svg>
                </button>
              )}
            </div>
          ) : (
            <button 
              className="btn-connect"
              onClick={() => !isDemoUser && onConnectAdSense(site)}
              disabled={isDemoUser}
            >
              Connect
            </button>
          )}
        </div>

        <div className="connection-item">
          <div className="connection-status">
            <div className={`status-indicator ${hasSearchConsole ? 'connected' : 'disconnected'}`} />
            <span>Google Search Console</span>
          </div>
          {hasSearchConsole ? (
            <div className="connection-actions">
              <span className="connection-name">
                {site.searchConsoleSiteUrl || site.searchConsoleUrl}
              </span>
              {!isDemoUser && (
                <button
                  className="btn-reconnect"
                  onClick={() => onConnectGSC(site)}
                  title="Reconnect Search Console"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"
                    />
                    <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z" />
                  </svg>
                </button>
              )}
            </div>
          ) : (
            <button className="btn-connect" onClick={() => !isDemoUser && onConnectGSC(site)} disabled={isDemoUser}>
              Connect
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteCard;

